import {
  loadDraft,
  saveDraft,
  clearDraft,
  getContextKey,
  loadTechSettings,
  saveTechSettings,
  purgeLegacyActiveModePointers,
} from '../../lib/render-settings-storage';
import {
  initialFormData,
  getExploreBaseline,
  getRefineBaseline,
  extractTechSettings,
  type RenderFormData,
} from './internals';
import type { DraftPersistenceSlice, RenderStateCreator } from './types';

export const createDraftPersistenceSlice: RenderStateCreator<
  DraftPersistenceSlice
> = (set, get) => ({
  initializeFormData: (
    values,
    conceptId,
    angleId,
    renderId,
    fallbackMode,
    parentRenderMode
  ) =>
    set((state) => {
      purgeLegacyActiveModePointers();
      const newKey = getContextKey(conceptId, angleId, renderId);
      // `parentRenderMode` describes the source render itself (for Explore
      // baseline blanking on a Refine source). `fallbackMode` describes
      // the active-tab default on first visit. Default `parentRenderMode`
      // to whatever `fallbackMode` was when the caller doesn't pass it
      // separately (preserves pre-refactor behavior).
      const effectiveParentMode = parentRenderMode ?? fallbackMode;

      // Merge server values with initial form data
      const serverSettings = { ...initialFormData, ...values };

      // Check if we need to reinitialize:
      // 1. Key changed (different concept/angle/render)
      // 2. Server settings changed (data loaded after initial mount)
      const keyChanged = state.lastInitializedKey !== newKey;
      const valuesChanged =
        !keyChanged &&
        JSON.stringify(state.defaultValues) !==
          JSON.stringify(serverSettings);

      if (!keyChanged && !valuesChanged) {
        return state; // No changes if already initialized with same key and values
      }

      // Explore is EPHEMERAL across contexts — the mode is never
      // persisted/restored. Entering a NEW context shows Refine whenever
      // there's a render to refine (renderId set); Explore is the only
      // option when there's nothing to refine. Explore is entered solely by
      // the in-session toggle, so switching render, angle, or concept (each
      // re-initializes with a NEW key) returns to Refine.
      //
      // A reload of the SAME context (server settings re-synced) is NOT a
      // context switch — resetting there would flip the user's in-session
      // Explore choice back to Refine mid-edit. Same key → keep the current
      // mode. Mirrors Apple `RenderManager.loadDraft` and Android
      // `RenderFormManager.resolveActiveMode`.
      const activeMode: RenderFormData['mode'] = keyChanged
        ? renderId
          ? 'refine'
          : 'explore'
        : state.formData.mode;

      // Load the draft for the resolved active mode (Explore/Refine drafts
      // are mode-keyed and stay independent).
      const draft = loadDraft(conceptId, angleId, renderId, activeMode);

      // Per RENDER FORM SPEC rule 1, the per-tab default depends on the
      // SOURCE RENDER's mode, not the active tab:
      //   - Source = Explore → Explore default = serverSettings, Refine default = blank
      //   - Source = Refine  → Explore default = blank,          Refine default = blank
      // i.e. a Refine source render must NOT seed the Explore tab.
      const refineBaseline = getRefineBaseline(serverSettings);
      const exploreBaseline =
        effectiveParentMode === 'refine' ? refineBaseline : serverSettings;
      const baselineForActiveMode =
        activeMode === 'refine' ? refineBaseline : exploreBaseline;

      // The technical group is a single app-wide per-user value: the slot
      // (written on every draft save) overrides whatever the mode draft or
      // baseline carried, and applies across every concept the user opens.
      // No slot = nothing edited yet (or a legacy install) → the mode draft's
      // own legacy values stand.
      const tech = loadTechSettings();

      const formData: RenderFormData = {
        ...baselineForActiveMode,
        ...draft,
        ...tech,
        mode: activeMode,
        parent_render_id: activeMode === 'refine' ? (renderId ?? null) : null,
      } as RenderFormData;

      return {
        lastInitializedKey: newKey,
        currentContext: { conceptId, angleId, renderId },
        defaultValues: serverSettings,
        // Freeze the slot the form just seeded its tech group from so the
        // change-detection baseline compares render style against the SAME
        // value (see `techBaseline` doc + `useSectionChanges`). `null` when no
        // slot exists yet → baseline falls back to the parent render's tech.
        techBaseline: tech,
        parentRenderMode: effectiveParentMode ?? 'explore',
        formData,
      };
    }),

  loadReadOnlySnapshot: (
    values,
    conceptId,
    angleId,
    renderId,
    parentRenderId
  ) =>
    set(() => {
      // Merge over initial form data so absent keys carry defaults. No draft
      // load (this is a non-editable readout) and no persist — the host locks
      // the panel so the user never edits these values.
      const serverSettings = { ...initialFormData, ...values };
      return {
        lastInitializedKey: getContextKey(conceptId, angleId, renderId),
        currentContext: { conceptId, angleId, renderId },
        defaultValues: serverSettings,
        // Read-only readout: the form mirrors the in-flight render's own
        // settings (no slot overlay), so the baseline must compare against
        // that same parent tech — `null` makes it fall back to `defaultValues`.
        techBaseline: null,
        parentRenderMode: 'refine',
        formData: {
          ...serverSettings,
          mode: 'refine',
          parent_render_id: parentRenderId,
        } as RenderFormData,
      };
    }),

  resetChangesToDefaults: () => {
    const state = get();
    if (!state.defaultValues) return;

    const currentMode = state.formData.mode;
    const parentRenderId = state.formData.parent_render_id;

    // Clear ONLY the current mode's draft — the other mode is independent
    // and must survive a reset of this one. The technical slot is NOT
    // cleared: it is a single app-wide per-user value (model/effort/
    // extended-thinking/output-style/Hi-Res), excluded from the change
    // indicator, so a design-form reset must leave the user's engine/output
    // preference untouched here and everywhere else.
    if (state.currentContext?.angleId) {
      const { conceptId, angleId, renderId } = state.currentContext;
      clearDraft(conceptId, angleId, renderId, currentMode);
    }

    // Refine resets to a blank design form (point-edit baseline); Explore
    // resets to its mode-aware baseline. Mode itself is preserved across
    // reset. The TECHNICAL group is carried over from the current form (it
    // equals the app-wide per-user slot) rather than snapped to the baseline,
    // so resetting the design fields never disturbs the user's engine/output
    // preference.
    //
    // Explore reset uses `getExploreBaseline()` so a Refine source does
    // NOT seed Explore on reset (RENDER FORM SPEC rule 1) — without this
    // split the reset would re-leak the parent's refine prompt into the
    // Explore tab the moment the user clicks "Reset".
    const preservedTech = extractTechSettings(state.formData);
    const resetForm: RenderFormData =
      currentMode === 'refine'
        ? {
            ...getRefineBaseline(state.defaultValues),
            ...preservedTech,
            // Preserve seed across reset — user did not ask for a re-roll.
            seed: state.formData.seed,
            is_derivative: state.formData.is_derivative,
            mode: 'refine',
            parent_render_id: parentRenderId,
          }
        : {
            ...initialFormData,
            ...getExploreBaseline(state.defaultValues, state.parentRenderMode),
            ...preservedTech,
            mode: 'explore',
            parent_render_id: null,
          };

    set({
      formData: resetForm,
      // Keep references as they were loaded
      selectedReferences: state.selectedReferences,
    });
  },

  persistDraft: () => {
    const state = get();
    if (!state.currentContext || !state.defaultValues) return;

    const { conceptId, angleId, renderId } = state.currentContext;

    // Ensure we have valid IDs before proceeding
    if (!conceptId || !angleId) return;

    // Per-mode baseline (RENDER FORM SPEC rule 1 + 2). The draft must
    // store only the user's delta over the tab's baseline:
    //   - Refine: starts blank (with parent's seed/is_derivative carried
    //     over). Anything the user adds counts as their delta.
    //   - Explore: starts at `getExploreBaseline()` — equal to
    //     defaultValues when the source render is Explore, blank when the
    //     source is Refine. Without this split, an Explore tab opened on
    //     a Refine source would persist EVERY field as "different from
    //     refine defaults" and balloon the saved draft.
    const currentMode = state.formData.mode;
    const baseline: Partial<RenderFormData> =
      currentMode === 'refine'
        ? getRefineBaseline(state.defaultValues)
        : getExploreBaseline(state.defaultValues, state.parentRenderMode);

    // Calculate draft (only changed fields). Skip mode/parent_render_id —
    // mode is encoded in the storage key, not the payload, and
    // parent_render_id is contextual (carousel selection).
    const draft: Partial<RenderFormData> = {};
    for (const key in state.formData) {
      if (key === 'mode' || key === 'parent_render_id') continue;
      const formValue = state.formData[key as keyof RenderFormData];
      const defaultValue = baseline[key as keyof RenderFormData];

      // Only include if different from baseline
      if (JSON.stringify(formValue) !== JSON.stringify(defaultValue)) {
        draft[key as keyof RenderFormData] = formValue as never;
      }
    }

    // Save under the form's CURRENT mode key — the toggle handler routes
    // explicit mode swaps; here we just persist the active form. saveDraft
    // strips the technical group from the mode-keyed payload; the per-user
    // tech slot below is its single home (one value app-wide, both modes).
    saveDraft(
      conceptId,
      angleId,
      renderId,
      state.formData.mode,
      draft,
      baseline
    );
    saveTechSettings(extractTechSettings(state.formData));
  },

  setRefineMode: (mode, parentRenderId) => {
    const state = get();
    const oldMode = state.formData.mode;

    // Same-mode toggle: just refresh parent_render_id (carousel may have moved).
    if (oldMode === mode) {
      set({
        formData: {
          ...state.formData,
          // Drop parent_render_id under 'explore' so the create payload stays
          // clean — server's required_if only fires under 'refine'.
          parent_render_id: mode === 'refine' ? parentRenderId : null,
        },
      });
      return;
    }

    // Drafts are mode-keyed: persist current form under the OLD mode key,
    // then either restore the NEW mode's draft or fall back per mode.
    // Refine starts blank (point edits over the parent render); Explore
    // starts from server-loaded defaults.
    const conceptId = state.currentContext?.conceptId;
    const angleId = state.currentContext?.angleId;
    const renderId = state.currentContext?.renderId;

    if (conceptId && angleId && state.defaultValues) {
      // Per-mode baselines (RENDER FORM SPEC rule 1). Drafts are stored as a
      // delta over the MODE-specific baseline, not over `defaultValues` — so
      // the outgoing diff and the incoming merge must both anchor on the
      // matching baseline. Using `defaultValues` here would leak the parent
      // Explore render's values (e.g. `use_auto_*_refs: true`,
      // `style_ids: [...]`) into the Refine form on every toggle, and would
      // also corrupt the persisted refine draft (fields equal to refine
      // baseline but ≠ defaults would be saved every time). Mirrors Apple
      // `RenderManager.setRefineMode` (which writes/reads stored drafts as
      // a complete state) and Android `RenderFormManager.setRefineMode`.
      const oldBaseline: Partial<RenderFormData> =
        oldMode === 'refine'
          ? getRefineBaseline(state.defaultValues)
          : getExploreBaseline(state.defaultValues, state.parentRenderMode);
      const newBaseline: Partial<RenderFormData> =
        mode === 'refine'
          ? getRefineBaseline(state.defaultValues)
          : getExploreBaseline(state.defaultValues, state.parentRenderMode);

      // Save current form under OLD mode
      const outgoing: Partial<RenderFormData> = {};
      for (const key in state.formData) {
        if (key === 'mode' || key === 'parent_render_id') continue;
        const formValue = state.formData[key as keyof RenderFormData];
        const defaultValue = oldBaseline[key as keyof RenderFormData];
        if (JSON.stringify(formValue) !== JSON.stringify(defaultValue)) {
          outgoing[key as keyof RenderFormData] = formValue as never;
        }
      }
      saveDraft(conceptId, angleId, renderId, oldMode, outgoing, oldBaseline);
      // Refresh the per-user tech slot from the outgoing form — the toggle
      // must hand the SAME technical choices to the incoming mode (and they
      // remain the user's app-wide engine/output preference).
      saveTechSettings(extractTechSettings(state.formData));

      // Load the NEW mode's draft (if any)
      const incoming = loadDraft(conceptId, angleId, renderId, mode);

      let nextForm: RenderFormData;
      if (incoming) {
        nextForm = {
          ...newBaseline,
          ...incoming,
          mode,
          parent_render_id: mode === 'refine' ? parentRenderId : null,
        } as RenderFormData;
      } else if (mode === 'refine') {
        // Refine starts with a blank design form — user does only point
        // edits over the parent.
        nextForm = {
          ...getRefineBaseline(state.defaultValues),
          // Preserve seed across the toggle — the user did not ask for a
          // re-roll, only a mode change.
          seed: state.formData.seed,
          is_derivative: state.formData.is_derivative,
          mode: 'refine',
          parent_render_id: parentRenderId,
        };
      } else {
        // Explore baseline is `defaultValues` when source is Explore, blank
        // when source is Refine (RENDER FORM SPEC rule 1) — same anchor used
        // by `initializeFormData` / `resetChangesToDefaults`. Without this
        // split a Refine source would seed the Explore tab on every toggle.
        nextForm = {
          ...initialFormData,
          ...getExploreBaseline(state.defaultValues, state.parentRenderMode),
          mode: 'explore',
          parent_render_id: null,
        } as RenderFormData;
      }

      // The technical group is SHARED between modes — carry it from the
      // outgoing form, overriding whatever the stored draft or baseline
      // carried, so the toggle never swaps the engine/output choices the
      // user just made.
      nextForm = { ...nextForm, ...extractTechSettings(state.formData) };

      set({ formData: nextForm });
      return;
    }

    // No context yet — fall through to a plain mode flip.
    set({
      formData: {
        ...state.formData,
        mode,
        parent_render_id: mode === 'refine' ? parentRenderId : null,
      },
    });
  },
});
